import { Injectable } from '@angular/core';

export interface ISmartLinkTriggerRunOptions {
  // Set by the status bar's own button: the run must never fall back to saving the article, so a
  // trigger that cannot run without a save declines instead (see runAll()).
  skipSave?: boolean;
}

// One panel's smart-link run, as the content status bar sees it.
export interface ISmartLinkTrigger {
  isBusy(): boolean;
  canRun(): boolean;
  run(options?: ISmartLinkTriggerRunOptions): void;
}

// Panels register themselves so the status bar's Smart Link button can run every panel that
// offers one, without its template knowing which panels exist.
@Injectable({
  providedIn: 'root',
})
export class SmartLinkTriggersService {
  private readonly triggers = new Map<string, ISmartLinkTrigger>();

  register(id: string, trigger: ISmartLinkTrigger): void {
    this.triggers.set(id, trigger);
  }

  // A panel destroyed after its replacement registered must not delete the replacement's entry.
  unregister(id: string, trigger: ISmartLinkTrigger): void {
    if (this.triggers.get(id) !== trigger) {
      return;
    }
    this.triggers.delete(id);
  }

  // Read on every change-detection pass, so it allocates nothing.
  isAnyBusy(): boolean {
    let isBusy = false;
    this.triggers.forEach((trigger) => (isBusy = isBusy || trigger.isBusy()));
    return isBusy;
  }

  // Panels re-check these guards inside run(), so a trigger that declines is its own no-op.
  runAll(options?: ISmartLinkTriggerRunOptions): void {
    this.triggers.forEach((trigger) => {
      if (trigger.canRun() && !trigger.isBusy()) {
        trigger.run(options);
      }
    });
  }

  list(): ISmartLinkTrigger[] {
    return Array.from(this.triggers.values());
  }
}
